import { CircularProgress } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTheme } from '@material-ui/styles';
import {
  Order,
  useDashboardAnalytics,
} from 'frontend/src/contexts/DashboardAnalytics';
import React from 'react';
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { providerName } from '../../../../utils';
import InfoIconWithTooltip from '../../../BuildingBlocks/InfoIconWithTooltip';
import Card from '../../Card';

const useStyles = makeStyles((theme: any) => ({
  cornerIcon: {
    position: 'absolute',
    top: theme.spacing(2.5),
    right: theme.spacing(2.25),
  },
  chartContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    position: 'relative',
  },
}));

const MILLIS_IN_DAY = 60 * 60 * 24 * 1000;

function GoodsSoldGraph() {
  const classes = useStyles({});
  const theme: any = useTheme();
  const { orders, isLoading } = useDashboardAnalytics();
  const data = getData(orders);
  function getData(orders: Order[]) {
    if (!orders?.length) return [];

    const startDate = new Date(orders[0].date);
    const endDate = new Date(orders[orders.length - 1].date);
    const days = [];
    for (let day = startDate; day <= endDate; day.setDate(day.getDate() + 1)) {
      days.push(new Date(day));
    }

    return orders
      ? days.map((day) => {
          const dateStr = day.toISOString().substring(0, 10);
          const order = orders.find(
            (order) => order.date.substring(0, dateStr.length) === dateStr,
          );
          return { date: dateStr, quantity: order ? order.quantity : 0 };
        })
      : [];
  }

  function calculateMovingAverage(data, period) {
    const dataLength = data.length;

    if (!dataLength) return;

    for (let i = period - 1; i < dataLength + period; i++) {
      let sum = 0;
      for (let j = i; j > i - period; j--) {
        sum += parseInt(data?.[j]?.quantity ?? data[dataLength - 1].quantity);
      }
      const average = Math.round(sum / period);
      const date =
        data[i]?.date ??
        new Date(Date.now() + i * MILLIS_IN_DAY).toISOString().slice(0, 10);
      if (data[i]) {
        data[i].average = average;
      } else {
        data.push({ date, quantity: null, average });
      }
    }
  }
  calculateMovingAverage(data, 30);

  return (
    <Card title={'Goods sales prediction'} noPadding={true}>
      <div className={classes.cornerIcon}>
        <InfoIconWithTooltip
          helpText={`${providerName}'s goods sales prediction.`}
        />
      </div>
      {isLoading && <CircularProgress />}
      {!isLoading && (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data}>
            <CartesianGrid stroke="#ccc" />
            <XAxis dataKey="date" />
            <YAxis />
            <Legend verticalAlign="top" height={36} />
            <Line
              type="monotone"
              name={'Goods Sold'}
              connectNulls
              dataKey="quantity"
              stroke={
                theme.palette.analyticsColors[
                  theme.palette.analyticsColors.length - 1
                ]
              }
              dot={false}
              strokeWidth={2}
            />
            <Tooltip labelFormatter={(val) => `Date: ${val}`} />
            <Line
              type="monotone"
              name={'Moving Average'}
              dataKey="average"
              stroke={theme.palette.brand.blueDeepSeaAnalytic}
              strokeWidth={3}
              dot={false}
            />
            <Brush startIndex={data.length - 180} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

export default GoodsSoldGraph;
